import {
  INK,
  MIN_TEXT,
  PAPER,
  coachColour,
  contrastRatio,
  hexToRgb,
  hslToRgb,
  labelOnFill,
  ramp,
  relativeLuminance,
  rgbToHex,
  rgbToHsl,
} from "./contrast-gate";

/**
 * The coach's colour takes orange's ONE JOB on a pay page (rules 144 and 145, gate 3.5).
 *
 * Mo, 8 September 2026: "I don't want the whole page, but apply our system to the colour of the
 * trainers." So this is not the old wash coming back. The page is still paper, the surfaces are
 * still hairlines, the status words are still semantic and the ghosts are still ink. What changes
 * hands is the single job orange holds on the screen: the one primary action, its hover, its
 * pressed and disabled states, the focus line, the tints behind a secondary, and orange-as-text.
 * Thirteen variables, and only the ones the design system already calls "the action".
 *
 * What is deliberately NOT written: the brand ramp. The Gymkee mark in the brand bar reads the
 * ramp's 500 step directly and stays Gymkee orange under every coach colour, because a product
 * mark carries the brand and not the screen's primary (rule 145, rule 337). Nothing writes
 * `secondaryColor` either: that was the old page wash and it is read by nothing.
 *
 * The label on the fill is never assumed. `labelOnFill` decides: ink when ink clears 4.5:1 on the
 * coach's hex, paper otherwise. A coach who picks a pale yellow gets ink; one who picks a navy
 * gets paper; neither is a judgement call made in a component.
 *
 * The model is coach web's `orangeJobVariables` (src/utilities/styles/tailwind.ts). Every value
 * is theirs except these four, and nothing else diverges:
 *
 *   1. `--gk-action-focus` and `--gk-line-orange` take the gated text step for the ground instead
 *      of ramp 700. Ramp 700 is a fixed fraction of the coach's own lightness and says nothing
 *      about the ground it is drawn on: measured, a slate focus ring came out at 1.94 to 1 on the
 *      dark ground and a yellow one at 2.76 on paper. Rule 13.2. Coach web has the same hole.
 *   2. `--gk-text-orange-ink` has to clear the gate on the tint of the fill as well as on the
 *      page, because a secondary button is a tint of the colour under a label of it. Coach web
 *      gates it on the page alone, which is why "Add a card" measured 4.33 to 1 on the slate and
 *      4.35 on the navy in dark, 4.16 on hover. Rule 13.2. Coach web has the same hole.
 *   3. The tints are an eight-digit hex, not `color-mix()`. `src/stripe/appearance.ts` reads
 *      `--gk-tint-orange15` back out with `getComputedStyle` and hands it to Stripe, and a custom
 *      property is not computed to a colour, so Stripe got the literal string, rejected the rule
 *      and collapsed the card fields. Same colour, a form every consumer can parse.
 *   4. There is a fourteenth value coach web has no equivalent for: the label a disabled primary
 *      needs, decided against the diluted fill. It cannot be a variable, because the token set
 *      carries no name for it, so it is published as a data attribute on the root and the
 *      stylesheet picks between two tokens that do exist. Filed as canon ask PAY-12; when the
 *      token lands this becomes an ordinary variable and the attribute goes.
 *
 * `textOnGround` also walks in the direction that helps rather than always down, which is the
 * PAY-11 bug in the shared contrast gate, not a divergence from coach web.
 */

/** The thirteen job tokens, so removing them is exactly as complete as writing them. */
const JOB_VARIABLES = [
  "--gk-action-primary",
  "--gk-action-primary-hover",
  "--gk-action-primary-pressed",
  "--gk-action-primary-disabled",
  "--gk-action-focus",
  "--gk-line-orange",
  "--gk-text-on-orange",
  "--gk-text-orange-ink",
  "--gk-text-orange-ink-strong",
  "--gk-tint-orange6",
  "--gk-tint-orange10",
  "--gk-tint-orange12",
  "--gk-tint-orange15",
] as const;

const HEX = /^#?[0-9a-fA-F]{6}$/;

/** A hex a coach actually chose, normalised, or null. Anything else falls back to Gymkee orange. */
export function normaliseCoachHex(value?: string | null): string | null {
  const raw = value?.trim();
  if (!raw || !HEX.test(raw)) return null;
  return (raw.startsWith("#") ? raw : `#${raw}`).toUpperCase();
}

/**
 * The coach's colour as TEXT on a given ground, and on every other ground it is drawn on.
 *
 * `contrast-gate.ts` is canon and synced, so it is not edited here; but its `textOnPaper` only
 * ever walks lightness DOWN, which is right on paper and backwards on ink: a darker step is
 * further from a dark ground, not closer, so on the dark theme it exhausts its forty steps and
 * returns the ink itself, which is invisible on ink. That is a real bug in the shared file and
 * coach web inherits it; the canon ask is filed as PAY-11.
 *
 * Here the walk goes in whichever direction helps: down on a light ground, up on a dark one.
 *
 * It also has to clear more than one ground. Orange-as-text is not only drawn on the page: a
 * secondary button is a tint of the same colour under a label of it, so the label sits on the
 * tint and not on the paper behind it. Measured before this, "Add a card" came out at 4.33 to 1
 * on the slate and 4.35 on the navy in dark, and 4.16 on hover where the tint is heavier. So the
 * walk stops only when the candidate clears every ground it will be drawn on. Rule 13.2.
 */
export function textOnGround(
  hex: string,
  ground: string,
  ...alsoOn: string[]
): { text: string; ratio: number; derived: boolean } {
  const grounds = [ground, ...alsoOn];
  const worst = (colour: string) =>
    Math.min(...grounds.map((g) => contrastRatio(colour, g)));

  const base = worst(hex);
  if (base >= MIN_TEXT)
    return { text: hex.toUpperCase(), ratio: base, derived: false };

  const up = relativeLuminance(ground) <= 0.5;
  const hsl = rgbToHsl(hexToRgb(hex));
  for (let step = 1; step <= 40; step += 1) {
    const offset = step * 0.0125;
    const l = up ? Math.min(1, hsl.l + offset) : Math.max(0, hsl.l - offset);
    const candidate = rgbToHex(hslToRgb({ ...hsl, l }));
    const ratio = worst(candidate);
    if (ratio >= MIN_TEXT) return { text: candidate, ratio, derived: true };
  }
  const last = up ? PAPER : INK;
  return { text: last, ratio: worst(last), derived: true };
}

/**
 * The colour a disabled primary actually shows, computed the same way the stylesheet mixes it.
 *
 * A disabled primary is the fill at forty percent over the surface behind it, so the label has to
 * be chosen against THAT and not against the fill. The mix is opaque over opaque in sRGB, which is
 * a plain per-channel average at the given weight, so it can be reproduced exactly here.
 */
export function mixOver(fill: string, surface: string, weight: number): string {
  const a = hexToRgb(fill);
  const b = hexToRgb(surface);
  const at = (x: number, y: number) => x * weight + y * (1 - weight);
  return rgbToHex({ r: at(a.r, b.r), g: at(a.g, b.g), b: at(a.b, b.b) });
}

/** What a disabled primary actually shows: the fill at forty percent over its surface. */
export function disabledFill(fill: string, surface: string): string {
  return mixOver(fill, surface, 0.4);
}

/**
 * Which of the two label tokens a disabled primary needs, as an attribute value.
 *
 * The label a disabled primary needs is decided by the same gate as every other label, but it
 * cannot be written as a colour: it would need a token of its own and the token set does not carry
 * one, so a runtime that invented the name would be reading a variable the design system has never
 * defined. Filed as canon ask PAY-12. Until it lands, the gate's answer is published as an
 * attribute on the root and the stylesheet picks between the two labels the token set DOES carry,
 * which is the same decision reaching the same two colours by a name that exists.
 */
export const DISABLED_LABEL_ATTRIBUTE = "coachDisabledLabel";

export function coachDisabledLabel(
  hex: string,
  grounds: CoachGrounds,
): "ink" | "paper" {
  return labelOnFill(disabledFill(hex, grounds.disabled)).usesInk
    ? "ink"
    : "paper";
}

/** The two grounds the gate has to answer on: the page, and what a disabled primary dilutes into. */
export interface CoachGrounds {
  /** The page ground, which is the paper surface of the active theme. */
  page: string;
  /** The ground a disabled primary is mixed over: paper in light, the elevated tone in dark. */
  disabled: string;
}

export interface CoachColourReport {
  fill: string;
  /** The gate's answer for the label on the fill: ink or paper, never assumed. */
  label: string;
  labelRatio: number;
  text: string;
  textRatio: number;
}

/**
 * The thirteen values, for a hex and a theme. Pure, so it can be read in a test or a probe.
 *
 * The grounds are passed in rather than read from the document, because they are token values and
 * the token file is the only place that owns them.
 */
export function coachJobVariables(
  hex: string,
  grounds: CoachGrounds,
): Record<string, string> {
  const { fill, label, ramp: steps } = coachColour(hex);
  const dark = relativeLuminance(grounds.page) <= 0.5;
  /* Orange-as-text is drawn on the page AND on a tint of itself: `.ds-btn-secondary` fills with
     tint10 and hovers to tint15, so the heavier tint over the page is the harder of the two
     grounds and gating on it covers the lighter one. */
  const tintGround = mixOver(fill, grounds.page, 0.15);
  const asText = textOnGround(fill, grounds.page, tintGround).text;
  /*
   * The focus ring and the orange line are TEXT-WEIGHT marks on the page ground, not fills, so
   * they take the gated text step and not a ramp step. Ramp 700 is a fixed fraction of the coach's
   * own lightness, which says nothing about the ground: measured before this, a slate ring came
   * out at 1.94 to 1 on the dark ground and a yellow one at 2.76 to 1 on paper. The gated step is
   * by construction at or above 4.5 on whichever ground is active. Rule 13.2.
   */
  const line = asText;
  /*
   * The tints are resolved to a real colour, not left as `color-mix()`.
   *
   * Coach web writes `color-mix(in srgb, <hex> N%, transparent)` here and that is fine inside a
   * browser. It is not fine on this app: `src/stripe/appearance.ts` reads `--gk-tint-orange15`
   * with `getComputedStyle` and hands it to Stripe as the focus halo, and a custom property is
   * NOT computed to a colour, so Stripe received the literal `color-mix(...)` string, rejected the
   * rule, and rendered the card fields at almost zero width with their labels clipped to one
   * character. An eight-digit hex is the same colour and every consumer understands it.
   */
  const tint = (percent: number) => withAlpha(fill, percent);

  return {
    "--gk-action-primary": fill,
    "--gk-action-primary-hover": steps["600"],
    "--gk-action-primary-pressed": steps["700"],
    /* Coach web's `orangeJobVariables` uses ramp 300 for the disabled fill; parity, rule 18.1. */
    "--gk-action-primary-disabled": steps["300"],
    "--gk-action-focus": line,
    "--gk-line-orange": line,
    "--gk-text-on-orange": label,
    "--gk-text-orange-ink": asText,
    /* Coach web's value verbatim (tailwind.ts:44); parity, rule 18.1. */
    "--gk-text-orange-ink-strong": dark ? steps["200"] : steps["800"],
    "--gk-tint-orange6": tint(6),
    "--gk-tint-orange10": tint(10),
    "--gk-tint-orange12": tint(12),
    "--gk-tint-orange15": tint(15),
  };
}

/**
 * Whether a hex can hold the action at all.
 *
 * `labelOnFill` returns the better of ink and paper on the fill. When even that is under 4.5 there
 * is no readable label for a primary in this colour, and the honest answer is to refuse rather
 * than to ship a button nobody can read. A rose at 4.32 against ink and 4.15 against paper is the
 * shape of the case. Rule 4.3.
 */
export function isUsableCoachColour(hex?: string | null): boolean {
  const normalised = normaliseCoachHex(hex);
  if (normalised === null) return false;
  const label = labelOnFill(normalised);
  if (label.ratio >= MIN_TEXT) return true;
  refuseOnce(normalised);
  return false;
}

/** Hexes already refused, so the warning is said once and not on every render. */
const refused = new Set<string>();

/**
 * Say a refusal out loud, once per colour.
 *
 * Both the hook and `applyCoachColour` can be the first to notice, depending on which runs first,
 * so the message lives here and the set makes the order not matter.
 */
function refuseOnce(hex: string): void {
  if (refused.has(hex)) return;
  refused.add(hex);
  console.warn(
    `Coach colour ${hex} refused: neither label reaches 4.5 to 1 on it ` +
      `(ink ${contrastRatio(hex, INK)} to 1, paper ${contrastRatio(hex, PAPER)} to 1). ` +
      `Falling back to the Gymkee action colour.`,
  );
}

/**
 * Give the page the coach's colour, or take it back.
 *
 * Called with no hex, with something that is not one, or with a colour the gate refuses, it
 * removes every variable it writes, so the page falls back to the Gymkee orange the token file
 * already carries. That is the path a coach who never picked a colour takes, and it is the same
 * code.
 */
export function applyCoachColour(
  hex?: string | null,
): CoachColourReport | null {
  if (typeof document === "undefined") return null;
  const root = document.documentElement;
  const normalised = normaliseCoachHex(hex);
  const clear = () => {
    for (const name of JOB_VARIABLES) root.style.removeProperty(name);
    delete root.dataset[DISABLED_LABEL_ATTRIBUTE];
    return null;
  };

  if (!normalised) return clear();

  const label = labelOnFill(normalised);
  if (label.ratio < MIN_TEXT) {
    refuseOnce(normalised);
    return clear();
  }

  const grounds = readGrounds(root);
  const variables = coachJobVariables(normalised, grounds);
  for (const [name, value] of Object.entries(variables)) {
    root.style.setProperty(name, value);
  }
  root.dataset[DISABLED_LABEL_ATTRIBUTE] = coachDisabledLabel(
    normalised,
    grounds,
  );

  const text = textOnGround(normalised, grounds.page);
  return {
    fill: normalised,
    label: label.label,
    labelRatio: label.ratio,
    text: text.text,
    textRatio: text.ratio,
  };
}

/**
 * The grounds, read off the root so they come from the token file and not from a copy of it here.
 *
 * The fallbacks are the two the gate already carries, which are the light and dark paper values,
 * so a document that has not applied its stylesheet yet still gets a sane answer.
 */
function readGrounds(root: HTMLElement): CoachGrounds {
  const computed = getComputedStyle(root);
  const read = (name: string, fallback: string) => {
    const value = computed.getPropertyValue(name).trim();
    return HEX.test(value) ? value : fallback;
  };
  const dark = root.dataset.theme === "dark";
  const page = read("--gk-surface-paper", dark ? INK : PAPER);
  return {
    page,
    /* Light dilutes into the page itself; dark dilutes into the elevated tone, which is what the
       copied dark rule for a disabled primary mixes against. */
    disabled: dark ? read("--gk-surface-paper-muted", page) : page,
  };
}

/**
 * The coach's colour at an alpha, as an eight-digit hex.
 *
 * Written as the six-digit colour plus a two-digit alpha channel rather than a channel function,
 * because the value is read back out of the custom property by Stripe's appearance helper and has
 * to be a plain colour it can parse. Nothing here is a typed colour: `fill` is the coach's own
 * hex and the alpha is arithmetic.
 */
function withAlpha(hex: string, percent: number): string {
  const channel = Math.round((percent / 100) * 255)
    .toString(16)
    .padStart(2, "0")
    .toUpperCase();
  return `${rgbToHex(hexToRgb(hex))}${channel}`;
}

/** Exported for the dev fixture and for anything that wants the ramp without applying it. */
export { ramp as coachRamp };
